import type { FilterFn } from "@tanstack/vue-table";
import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Button from "../../button/Button";
import Checkbox from "../../checkbox/Checkbox";
import { injectTable } from "../DataTable";
import { defaultFacetedProps } from "./props";

export type BooleanFilter = {
  _type: "boolean";
  operator: "eq";
  value: any;
};

export function formatBooleanFilter(value: any): BooleanFilter {
  return {
    _type: "boolean",
    operator: "eq",
    value,
  };
}

const booleanProps = {
  ...defaultFacetedProps,
  transform: {
    type: Function,
    required: false,
  },
  operator: p.string<"eq">("eq"),
  onChange: {
    type: Function,
    required: false,
  },
} satisfies ComponentPropsOptions;

export type BooleanFacetedFilterProps = ExtractPublicPropTypes<typeof booleanProps>;

export default defineComponent({
  name: "BooleanFacetedFilter",
  props: booleanProps,
  setup(props) {
    const table = injectTable();
    const column = table.getColumn(props.accessorKey);

    const checked = ref(false);

    const columnFilter = computed(() => {
      const filter = column?.getFilterValue();
      if (!isBooleanFilter(filter)) return undefined;
      return filter;
    });

    onMounted(() => {
      if (columnFilter.value) {
        checked.value = true;
      }
    });

    watch(columnFilter, (val) => {
      if (!val) {
        checked.value = false;
      }
    });

    watch(checked, (val) => {
      if (!column) return;

      if (!val) {
        column.setFilterValue(null);
        props.onChange?.(null);
        return;
      }

      const value = props.transform ? props.transform() : true;
      column.setFilterValue(formatBooleanFilter(value));
      props.onChange?.(value);
    });

    function toggle() {
      checked.value = !checked.value;
    }

    return () => (
      <Button
        variant={checked.value ? "outline" : "dashed"}
        class={cn("h-8 px-3")}
        onClick={toggle}
      >
        <span class="center gap-2">
          <Checkbox modelValue={checked.value} size="sm" class="pointer-events-none" />
          {props.label}
        </span>
      </Button>
    );
  },
});

export function isBooleanFilter(filter: unknown): filter is BooleanFilter {
  if (typeof filter !== "object") return false;
  if (!filter) return false;

  return "_type" in filter && filter._type === "boolean";
}

export const booleanFilter: FilterFn<any> = (row, id, comparator: BooleanFilter) => {
  if (!comparator) return true;

  const rowValue = row.getValue(id);

  return rowValue === comparator.value;
};
